import { Typography } from "@kaltura/ds-react-components";
import { translatePlural } from "@kaltura/mediaspace-shared-utils";
import { TitlePlaylistContainer } from "./styledComponents";
import { EmptyState } from "./empty-state/EmptyState";
import { useState, useEffect } from "react";
import { EditPlaylistTabs, MediaType } from "./types";
import { AddToPlaylist, AddToPlaylistProps } from "./add-to-playlist/AddToPlaylist";
import { ContentList } from "./content-list/ContentList";
import { PlaylistInfo, PlaylistInfoProps } from "./info/PlaylistInfo";
import { ButtonAnalyticsProvider } from "@kaltura/mediaspace-shared-hooks";

/** media list response - contains success status and media data to render */
export type MediaListResponse = {
    success: boolean;
    media: MediaType[];
};

export interface EditPlaylistProps
    extends PlaylistInfoProps,
        Pick<AddToPlaylistProps, "pageSize" | "tabs" | "appendingPosition"> {
    fetchMediaData: () => MediaListResponse;
    onMediaUpdate?: (updatedMedia: MediaType[]) => void;
    getMediaApiUrl: string;
    showTitle: boolean;
    activeTab: EditPlaylistTabs;
    isWatchLater: boolean;
}

/**
 * EditPlaylist Component - Internal component that displays the playlist content
 * Uses the data fetched via Suspense and manages the data internally
 */
export const EditPlaylist = (props: EditPlaylistProps) => {
    const {
        fetchMediaData,
        tabs,
        getMediaApiUrl,
        deleteUrl,
        postDeleteRedirectUrl,
        showTitle,
        activeTab,
        playlist,
        onInfoUpdate,
        onSave,
        onMediaUpdate,
        formValues,
        pageSize,
        isWatchLater,
        appendingPosition,
    } = props;

    // Get the data from the suspense fetch
    const data = fetchMediaData();

    // State for managing media list internally
    // Initialize with data from suspense fetch
    const [mediaList, setMediaList] = useState<MediaType[]>(data?.media || []);

    useEffect(() => {
        // This effect should run only once when the component mounts and the initial data is received
        onMediaUpdate?.(data?.media || []);
    }, [data, onMediaUpdate]);

    // Handle media updates internally
    const handleMediaUpdate = (updatedMedia: MediaType[]) => {
        setMediaList(updatedMedia);
        onMediaUpdate?.(updatedMedia);
    };

    return (
        <>
            {activeTab === "content" && (
                <ButtonAnalyticsProvider location={"Playlist edit - Content tab"}>
                    <TitlePlaylistContainer>
                        <Typography variant="body2">
                            {translatePlural("%1 Item", "%1 Items", [mediaList.length])}
                        </Typography>
                        {!isWatchLater && (
                            <AddToPlaylist
                                tabs={tabs}
                                getMediaApiUrl={getMediaApiUrl}
                                media={mediaList}
                                isEmptyPlaylistScreen={true}
                                onMediaAdded={handleMediaUpdate}
                                pageSize={pageSize}
                                playlistId={playlist.id}
                                appendingPosition={appendingPosition}
                            />
                        )}
                    </TitlePlaylistContainer>
                    {mediaList.length === 0 && (
                        <EmptyState
                            tabs={tabs}
                            getMediaApiUrl={getMediaApiUrl}
                            media={mediaList}
                            headingLevel={showTitle ? "h2" : "h1"}
                            onMediaAdded={handleMediaUpdate}
                            pageSize={pageSize}
                            playlistId={playlist.id}
                            enableAddItems={!isWatchLater}
                        />
                    )}
                    {mediaList.length > 0 && (
                        <ContentList media={mediaList} onMediaUpdate={handleMediaUpdate} enableDrag={!isWatchLater} />
                    )}
                </ButtonAnalyticsProvider>
            )}

            {activeTab === "info" && (
                <PlaylistInfo
                    playlist={playlist}
                    onInfoUpdate={onInfoUpdate}
                    onSave={onSave}
                    formValues={formValues}
                    deleteUrl={deleteUrl}
                    postDeleteRedirectUrl={postDeleteRedirectUrl}
                />
            )}
        </>
    );
};

// Export as default as well for easier imports
export default EditPlaylist;
